import { Box, HStack, Progress, useDisclosure, useTheme, VStack } from '@chakra-ui/react';
import { differenceInDays, format } from 'date-fns';

import Button from 'components/Button';
import Icon from 'components/Icon';
import { CancelSubscriptionModal } from 'components/Modal/CancelSubscriptionModal';
import Text from 'components/Text';
import { DATE_FORMAT, plans } from 'config/constants/plans';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { Plan_Type_Enum } from 'types/databaseEnums';

interface ITrialState {
  plan: Plan_Type_Enum;
  trialEndDate: Date;
  amount: number;
}

export const TrialState = ({ plan, trialEndDate, amount }: ITrialState) => {
  const theme = useTheme();
  const { isDesktopCancelSubscriptionButton } = useMediaQueries();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const planDetails = plans[plan];

  const now = new Date();
  const daysRemaining = Math.max(0, differenceInDays(trialEndDate, now));
  const totalTrialDays = 7;
  const progressValue = ((totalTrialDays - daysRemaining) / totalTrialDays) * 100;

  return (
    <>
      <VStack align="stretch" spacing={6}>
        <VStack
          bg="whiteAlpha.200"
          borderRadius={theme.borderRadius}
          p={6}
          align="stretch"
          boxShadow="0px 10px 20px rgba(0, 0, 0, 0.15)"
        >
          <HStack align="stretch" justifyContent="space-between">
            <VStack align="stretch">
              <HStack spacing={3}>
                <Icon type={planDetails.icon} size={48} />
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="xLarge">{planDetails.title}</Text>
                  <Text fontSize="small" color="green.300" fontWeight="semibold">
                    Trial Active
                  </Text>
                </VStack>
              </HStack>
            </VStack>
            {isDesktopCancelSubscriptionButton && (
              <Button scheme="danger" leftIcon={<Icon type="Close" />} onClick={onOpen}>
                Cancel subscription
              </Button>
            )}
          </HStack>

        <Box mt={4}>
          <HStack justifyContent="space-between" mb={2}>
            <Text fontSize="small" opacity="secondary">
              Trial Progress
            </Text>
            <Text fontSize="small" fontWeight="semibold">
              {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
            </Text>
          </HStack>
          <Progress
            value={progressValue}
            size="sm"
            colorScheme="green"
            borderRadius="full"
            bg="whiteAlpha.200"
          />
        </Box>

        <VStack align="stretch" mt={4} spacing={2}>
          <HStack justifyContent="space-between">
            <Text fontSize="small" opacity="secondary" fontWeight="regular">
              Trial ends on
            </Text>
            <Text fontSize="small">{format(trialEndDate, DATE_FORMAT)}</Text>
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="small" opacity="secondary" fontWeight="regular">
              Amount after trial
            </Text>
            <Text fontSize="small">
              ${amount ? amount / 100 : 0}/{plan.toLowerCase()}
            </Text>
          </HStack>
        </VStack>

        {!isDesktopCancelSubscriptionButton && (
          <Box pt={3}>
            <Button
              leftIcon={<Icon type="Close" />}
              scheme="danger"
              width="fit-content"
              onClick={onOpen}
            >
              Cancel subscription
            </Button>
          </Box>
        )}
      </VStack>

      <VStack
        bg="blue.900"
        borderRadius={theme.borderRadius}
        px={6}
        py={4}
        align="flex-start"
        borderWidth={1}
        borderColor="blue.700"
      >
        <HStack spacing={2}>
          <Icon type="NotificationBell" size={16} />
          <Text fontSize="small" fontWeight="semibold">
            What happens next?
          </Text>
        </HStack>
        <Text fontSize="small" fontWeight="regular" opacity="secondary">
          Your card will be charged ${amount ? amount / 100 : 0} on{' '}
          {format(trialEndDate, DATE_FORMAT)} when your trial ends. You can cancel anytime before
          then to avoid charges.
        </Text>
      </VStack>
    </VStack>
    <CancelSubscriptionModal isOpen={isOpen} onClose={onClose} />
  </>
  );
};
